import React from "react";
import { useTranslation } from "react-i18next";
import { ApiStatisticsResponse } from "../api/apiStatistics";

interface ApiStatisticsDashboardProps {
    data: ApiStatisticsResponse;
}

export const ApiStatisticsDashboard: React.FC<ApiStatisticsDashboardProps> = ({
    data,
}) => {
    const { t } = useTranslation("apiStatistics");

    const getMethodColor = (method: string) => {
        switch (method.toUpperCase()) {
            case "GET":
                return "bg-green-100 text-green-800";
            case "POST":
                return "bg-blue-100 text-blue-800";
            case "PUT":
                return "bg-yellow-100 text-yellow-800";
            case "DELETE":
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const getStatusColor = (code: string) => {
        const codeNum = parseInt(code);
        if (codeNum >= 200 && codeNum < 300) return "text-green-600";
        if (codeNum >= 400 && codeNum < 500) return "text-yellow-600";
        if (codeNum >= 500) return "text-red-600";
        return "text-gray-600";
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString("ru-RU");
    };

    const formatResponseTime = (time: number) => {
        return `${time.toFixed(2)}ms`;
    };

    return (
        <div className="space-y-6">
            {/* Основная статистика */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                                <span className="text-white text-sm font-medium">
                                    📊
                                </span>
                            </div>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {t("total_requests")}
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {data.total_requests}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                                <span className="text-white text-sm font-medium">
                                    ⚡
                                </span>
                            </div>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {t("avg_response_time")}
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {formatResponseTime(
                                    data.average_response_time_ms
                                )}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                                <span className="text-white text-sm font-medium">
                                    ❌
                                </span>
                            </div>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {t("error_count")}
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {data.error_count}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                                <span className="text-white text-sm font-medium">
                                    📅
                                </span>
                            </div>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                {t("date")}
                            </p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                                {formatDate(data.date)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Методы HTTP */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {t("http_methods")}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {Object.entries(data.methods).map(([method, count]) => (
                        <div
                            key={method}
                            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
                        >
                            <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getMethodColor(
                                    method
                                )}`}
                            >
                                {method}
                            </span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {count}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Коды статусов */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {t("status_codes")}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
                    {Object.entries(data.status_codes).map(([code, count]) => (
                        <div
                            key={code}
                            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
                        >
                            <span
                                className={`text-sm font-medium ${getStatusColor(
                                    code
                                )}`}
                            >
                                {code}
                            </span>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {count}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Популярные эндпоинты */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {t("popular_endpoints")}
                </h3>
                <div className="space-y-3">
                    {Object.entries(data.endpoints)
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 10)
                        .map(([endpoint, count]) => (
                            <div
                                key={endpoint}
                                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
                            >
                                <span className="text-sm font-mono text-gray-900 dark:text-white truncate">
                                    {endpoint}
                                </span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {count}
                                </span>
                            </div>
                        ))}
                </div>
            </div>

            {/* IP адреса */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {t("ip_addresses")}
                </h3>
                <div className="space-y-3">
                    {Object.entries(data.ip_addresses)
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 10)
                        .map(([ip, count]) => (
                            <div
                                key={ip}
                                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
                            >
                                <span className="text-sm font-mono text-gray-900 dark:text-white">
                                    {ip}
                                </span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {count}
                                </span>
                            </div>
                        ))}
                </div>
            </div>

            {/* API ключи */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {t("api_keys")}
                </h3>
                <div className="space-y-3">
                    {Object.entries(data.api_keys)
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 10)
                        .map(([key, count]) => (
                            <div
                                key={key}
                                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
                            >
                                <span className="text-sm font-mono text-gray-900 dark:text-white truncate">
                                    {key === "unknown" ? t("unknown") : key}
                                </span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {count}
                                </span>
                            </div>
                        ))}
                </div>
            </div>

            {/* Ошибки */}
            {data.errors.length > 0 && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                        {t("errors")}
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 dark:bg-gray-900">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {t("timestamp")}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {t("method")}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {t("url")}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {t("status_code")}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {t("response_size")}
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {t("api_key")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                                {data.errors.map((error) => (
                                    <tr
                                        key={error.request_id}
                                        className="hover:bg-gray-50 dark:bg-gray-900"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                            {error.timestamp}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span
                                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getMethodColor(
                                                    error.method
                                                )}`}
                                            >
                                                {error.method}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-mono">
                                            {error.url}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span
                                                className={`text-sm font-medium ${getStatusColor(
                                                    error.status_code.toString()
                                                )}`}
                                            >
                                                {error.status_code}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {error.response_size} bytes
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-mono">
                                            {error.api_key === "unknown"
                                                ? t("unknown")
                                                : error.api_key}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};
